import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Select from "react-select";
import { Modal, Row, Col, Form } from 'react-bootstrap';
import { NumericFormat as NumberFormat } from "react-number-format";

import { deepEqual } from "../../../../../utils/object";
import { addMinutes, minutesBetween } from "../../../../../utils/date";
import { get as getMatters } from "../../../../../actions/matters";
import { get as getStaff, billable } from "../../../../../actions/staff";
import { get as getClock, save } from "../../../../../actions/clocks";

import DatePickerEx from "../../../../../layout/date-picker";
import ConditionalButton from "../../../../../layout/conditional-button";
import { isGuid } from "../../../../../utils/string";

class Timecard extends React.Component {
    /**
     *
     */
    constructor(props) {
        super(props);
        this.state = this.mapPropsToState(props);
    }

    toDate = (v) => (v instanceof Date ? v : v != null ? new Date(v) : null);

    mapPropsToState = (props) => {
        const {
            uuid, caseUuid, personUuid, staffUuid,
            started, ended, minutes, rate, gross, writeOff, net, notes, show
        } = props;

        const now = new Date();
        const s = this.toDate(started) ?? new Date(now.getTime() - 6 * 60 * 1000);
        const e = this.toDate(ended) ?? now;

        return {
            uuid,
            caseUuid,
            personUuid,
            staffUuid,
            started: s,
            ended: e,
            minutes: minutes ?? 6,
            rate,
            gross,
            writeOff,
            net,
            notes, 
            show,
            error: null
        };
    };

    componentDidMount = async (e) => {
        await this.loadData();
    }

    componentDidUpdate = (oldProps) => {
        const oldSlice = this.mapPropsToState(oldProps);
        const newSlice = this.mapPropsToState(this.props);
        if (deepEqual(oldSlice, newSlice)) return;
        this.setState(newSlice, this.loadData);
    }

    loadData = async () => {
        const { uuid, staffUuid, personUuid, caseUuid } = this.state;
        let base = {}; 
        if (isGuid(uuid)) {
            const clockData = await this.props.getClock({ uuid });
            base = {...clockData}
        }

        const matters = await this.props.getMatters({ staffUuid, personUuid });
        const staff = await this.props.getStaff({ uuid: staffUuid, personUuid, caseUuid });
        let billable = { minutes: 6, rate: 0 };

        if (isGuid(staffUuid)) {
            billable = await this.props.billable({ uuid: staffUuid, personUuid, caseUuid });
        }
        this.setState({
            matters: (matters?.items ?? []).map((r) => ({
                ...r,
                value: r.uuid,
                label: r.caption
            })),
            staff: (staff ?? []).map((r) => ({
                ...r,
                value: r.uuid,
                label: `${r.Person.displayName}`
            })),
            rate: billable?.rate,
            billable,
            ...base
        });
    };

    billedMinutes = (actualMinutes, minutesInterval = 6) => {
        const a = Number(actualMinutes) || 0;
        const i = Number(minutesInterval) || 1;
        if (i <= 0) return actualMinutes;
        return Math.ceil(a / i) * i;
    }

    doPostChange = (e) => {

    }

    onCancel = async (e) => {
        if (this.props.onCancel) this.props.onCancel(e);
    }

    onHideModal = (e) => {
        this.onCancel(e);
    }

    onChange = (e) => {
        const { name, value } = e.target;
        if (this.state[name] === value) return;

        this.setState({ [name]: value }, this.doPostChange);
    };

    onGrossChange = (e) => {
        this.setState(prev => ({
            net: e.target.value - prev.writeOff,
            gross: e.target.value
        }));
    }

    onWriteOffChange = (e) => {
        this.setState(prev => ({
            net: prev.gross - e.target.value,
            writeOff: e.target.value
        }));
    }

    onNetChange = (e) => {
        this.setState(prev => ({
            writeOff: prev.gross - Math.min(prev.gross, e.target.value),
            net: Math.min(prev.gross, e.target.value)
        }));
    }

    onStaffChange = async (e) => {
        const billable = await this.props.billable({ uuid: e.target.value, personUuid: this.state.personUuid ?? this.props.personUuid, caseUuid: this.state.caseUuid ?? this.props.caseUuid });
        if (!billable) return;

        const minutes = this.billedMinutes(this.state.minutes, billable.minutes);
        const gross = billable.rate * (minutes / 60);
        const net = gross - (this.state.writeOff ?? 0);

        this.setState({
            staffUuid: e.target.value,
            billable,
            rate: billable?.rate,
            minutes,
            gross,
            net
        }, this.doPostChange);
    }

    onClockChange = (field, value) => {
        const started = field === "started" ? new Date(value) : this.state.started;
        const ended = field === "ended" ? new Date(value) : this.state.ended;

        this.setState({
            started,
            ended,
            minutes: minutesBetween(started, ended)
        }, this.doPostChange);
    };

    onMinutesChange = (value) => {
        const mins = value?.floatValue ?? 0;
        const billing = {};

        if (this.state.billable) {
            billing.minutes = this.billedMinutes(mins, this.state.billable.minutes);
            billing.gross = this.state.rate * (billing.minutes / 60);
            billing.net = billing.gross - (this.state.writeOff ?? 0);
        }

        this.setState({
            started: addMinutes(this.state.ended, -mins),
            ...billing,
            minutes: mins,
        }, this.doPostChange);
    };

    onOk = async () => {
        const result = await this.props.save(this.state);
        if (result?.success === false || !result) {
            this.setState({
                error: result?.error?.message || "The billable time could not be saved."
            });
            return;
        }
        if (this.props.onOk) this.props.onOk(this.state);
    }

    get canSave() {
        return ((this.state.minutes > 0) && (isGuid(this.state.staffUuid)) && (this.state.notes?.length > 5));
    }

    render() {
        return (
            <>
                <Modal size="lg" backdrop="static" keyboard show={this.props.show} onHide={this.onHideModal}>
                    <Modal.Header closeButton>
                        <Modal.Title id="staticBackdropLabel">{this.props.title || `Billable Time`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3 pb-3 mt-1">
                            {this.state.error ? (
                                <Col xs="12">
                                    <div className="alert alert-danger mb-0">{this.state.error}</div>
                                </Col>
                            ) : null}
                            {!this.props.caseUuid &&
                                <Col xs="12">
                                    <div className="top-label">
                                        <Select
                                            id="caseUuid"
                                            value={this.state.matters?.find(option => option.value === this.state.caseUuid)}
                                            name="caseUuid"
                                            onChange={(e) => this.onChange(
                                                {
                                                    target: {
                                                        option: e,
                                                        name: "caseUuid",
                                                        value: e?.value
                                                    }
                                                }
                                            )}
                                            classNamePrefix="react-select"
                                            placeholder="Matter"
                                            options={this.state.matters}
                                            isClearable
                                        >
                                        </Select>
                                        <Form.Label>MATTER</Form.Label>
                                    </div>
                                </Col>
                            }
                            {!this.props.staffUuid &&
                                <>
                                    <Col xs="12" md={this.state?.rate > 0 ? 8 : 12}>
                                        <div className="top-label">
                                            <Select
                                                id="staffUuid"
                                                value={this.state.staff?.find(option => option.value === this.state.staffUuid)}
                                                name="staffUuid"
                                                onChange={(e) => this.onStaffChange(
                                                    {
                                                        target: {
                                                            option: e,
                                                            name: "staffUuid",
                                                            value: e.value
                                                        }
                                                    }
                                                )}
                                                classNamePrefix="react-select"
                                                placeholder="Staff Member"
                                                options={this.state.staff}
                                            >
                                            </Select>
                                            <Form.Label>STAFF MEMBER</Form.Label>
                                        </div>
                                    </Col>
                                    {(this.state?.rate > 0) &&
                                        <Col xs="12" md="4">
                                            <div className="top-label">
                                                <NumberFormat
                                                    displayType="input"
                                                    type="text"
                                                    id="rate"
                                                    name="rate"
                                                    className="text-end form-control mb-2"
                                                    prefix="$"
                                                    thousandSeparator={false}
                                                    allowNegative={false}
                                                    value={this.state.rate ?? 0}
                                                    onValueChange={(e) => this.onChange({ target: { name: "rate", value: e.floatValue } })}
                                                    removeFormatting
                                                    isAllowed={(values) => {
                                                        const { floatValue } = values;
                                                        return floatValue == null || floatValue <= 5000000;
                                                    }}
                                                />
                                                <Form.Label>BILLABLE RATE</Form.Label>
                                            </div>
                                        </Col>
                                    }
                                </>
                            }
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <DatePickerEx
                                        id="started"
                                        selected={this.state.started}
                                        name="started"
                                        onChange={(e) => this.onClockChange("started", e)}
                                        className="form-control"
                                        dateFormat="MM/dd/yyyy h:mm aa"
                                        timeInputLabel="Time:"
                                        showTimeInput
                                    />
                                    <Form.Label>FROM</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <DatePickerEx
                                        id="ended"
                                        selected={this.state.ended}
                                        name="ended"
                                        onChange={(e) => this.onClockChange("ended", e)}
                                        dateFormat="MM/dd/yyyy h:mm aa"
                                        timeInputLabel="Time:"
                                        showTimeInput
                                        className="form-control"
                                    />
                                    <Form.Label>TO</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="minutes"
                                        name="minutes"
                                        className="text-right form-control mb-2"
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        value={this.state.minutes || 0}
                                        onValueChange={this.onMinutesChange}
                                        removeFormatting
                                    />
                                    <Form.Label>MINUTES</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="gross"
                                        name="gross"
                                        className="text-end form-control mb-2"
                                        prefix="$"
                                        thousandSeparator={false}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={this.state.gross ?? 0}
                                        onValueChange={(e) => this.onGrossChange({ target: { name: "gross", value: e.floatValue } })}
                                        removeFormatting
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return floatValue == null || floatValue <= 5000000;
                                        }}
                                    />
                                    <Form.Label>GROSS</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="writeOff"
                                        name="writeOff"
                                        className="text-end form-control mb-2"
                                        prefix="$"
                                        thousandSeparator={false}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={this.state.writeOff ?? 0}
                                        onValueChange={(e) => this.onWriteOffChange({ target: { name: "writeOff", value: e.floatValue } })}
                                        removeFormatting
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return floatValue == null || floatValue <= this.state.gross;
                                        }}
                                    />
                                    <Form.Label>WRITE OFF</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="net"
                                        name="net"
                                        className="text-end form-control mb-2"
                                        prefix="$"
                                        thousandSeparator={false}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={this.state.net ?? 0}
                                        onValueChange={(e) => this.onNetChange({ target: { name: "net", value: e.floatValue } })}
                                        removeFormatting
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return floatValue == null || floatValue <= (this.state.gross - this.state.writeOff);
                                        }}
                                    />
                                    <Form.Label>NET</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12">
                                <div className="top-label">
                                    <Form.Control type="text" name="notes" value={this.state.notes || ""} onChange={this.onChange} />
                                    <Form.Label>NOTES</Form.Label>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onCancel} icon="error-hexagon">cancel</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled={this.canSave} onEnabledClick={this.onOk} icon="save">ok</ConditionalButton>
                    </Modal.Footer>
                </Modal>
            </>
        )
    }
}

Timecard.propTypes = {
    auth: PropTypes.object.isRequired
}

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default connect(mapStateToProps,
    {
        getMatters,
        getStaff,
        getClock,
        save,
        billable
    }
)(Timecard);
